/** The knowledge base — Enterprise Phase E, module 5, end-user side.
 *
 * A documentation portal rather than a list of links: a hero search that is the
 * first thing on the page, recently updated work, and an article page with a
 * contents rail, reading time and related reading.
 *
 * Four of the reading features the brief asks for are derived from the body
 * rather than waited for — contents, reading time, excerpt, related articles.
 * They are marked as derived where it matters, because a contents list computed
 * from headings is not the same promise as one an author curated.
 *
 * Two things the brief asks for that this cannot do, and says so rather than
 * faking: there are no categories (no field), and "was this helpful" has
 * nowhere to record an answer. The rating widget is rendered and disabled — a
 * thumbs-up that silently discards the click is worse than one that explains
 * itself.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BookOpen,
  Clock,
  FileDown,
  Link2,
  Printer,
  Search,
  Share2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Badge } from '../../components/ui/badge'
import { Breadcrumbs } from '../../components/ui/breadcrumbs'
import { Button } from '../../components/ui/button'
import { ErrorNote } from '../../components/ui/callout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { LiveRegion } from '../../components/ui/live-region'
import { Input, Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/primitives'
import { RichText } from '../../components/ui/rich-text'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import {
  feedbackMessage,
  feedbackState,
  isChosen,
  nextAnswer,
  type Answer,
  type FeedbackResponse,
} from '../../lib/articleFeedback'
import {
  TARGET_CATEGORIES,
  readingTime,
  recentlyUpdated,
  relatedArticles,
  tableOfContents,
  withHeadingIds,
  type PortalArticleDetail,
  type KbArticleSummary,
} from '../../lib/kb'
import { formatDate } from '../../lib/ui'
import { cn } from '../../lib/utils'

/* ------------------------------------------------------------- landing */

export function KbList() {
  const [term, setTerm] = useState('')
  const searching = term.trim().length >= 2

  const { data: all = [], isLoading } = useQuery({
    queryKey: ['kb'],
    queryFn: () => api.get<KbArticleSummary[]>('/kb/'),
  })

  // The server search is title-and-body and capped at ten; the local filter is
  // title-only over everything already loaded. Running both means a reader sees
  // an instant title match while the body match arrives.
  const { data: found = [], isFetching } = useQuery({
    queryKey: ['kb', term],
    queryFn: () => api.get<KbArticleSummary[]>(`/kb/?q=${encodeURIComponent(term)}`),
    enabled: searching,
  })

  const results = useMemo(() => {
    if (!searching) return []
    const needle = term.trim().toLowerCase()
    const local = all.filter((article) => article.title.toLowerCase().includes(needle))
    const merged = new Map(local.map((article) => [article.slug, article]))
    for (const article of found) merged.set(article.slug, article)
    return [...merged.values()]
  }, [all, found, searching, term])

  const recent = useMemo(() => recentlyUpdated(all), [all])

  return (
    <>
      {/* --- Hero ------------------------------------------------------- */}
      <section className="mb-6 rounded-lg border border-border bg-card p-6 text-center shadow-card sm:p-10">
        <BookOpen className="mx-auto size-7 text-primary" aria-hidden />
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">Knowledge base</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Guides and answers. Usually faster than raising a ticket.
        </p>

        <div className="relative mx-auto mt-5 max-w-xl">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search for VPN, password, printer…"
            aria-label="Search the knowledge base"
            className="h-11 pl-9 text-base"
          />
        </div>

        <p className="mt-2 text-xs text-muted-foreground">
          {all.length} article{all.length === 1 ? '' : 's'} available to you.
        </p>
      </section>

      <LiveRegion
        message={
          searching ? `${results.length} article${results.length === 1 ? '' : 's'} match.` : ''
        }
      />

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-32" />
          ))}
        </div>
      ) : searching ? (
        <section aria-label="Search results">
          <h2 className="mb-2.5 text-sm font-semibold tracking-tight">
            {results.length} result{results.length === 1 ? '' : 's'} for &ldquo;{term}&rdquo;
            {isFetching && <span className="ml-2 text-xs font-normal text-muted-foreground">searching…</span>}
          </h2>
          {results.length === 0 ? (
            <EmptyState
              icon={Search}
              title="Nothing found"
              description="Try fewer words, or a different one. If nothing here helps, raise a ticket and we will write the article."
              action={
                <Button asChild size="sm">
                  <Link to="/new">Raise a ticket</Link>
                </Button>
              }
            />
          ) : (
            <ArticleGrid articles={results} />
          )}
        </section>
      ) : (
        <>
          {recent.length > 0 && (
            <section aria-labelledby="kb-recent" className="mb-6">
              <h2 id="kb-recent" className="mb-2.5 flex items-center gap-2 text-sm font-semibold tracking-tight">
                <Clock className="size-3.5 text-muted-foreground" aria-hidden />
                Recently updated
              </h2>
              <ArticleGrid articles={recent} />
            </section>
          )}

          <section aria-labelledby="kb-all" className="mb-6">
            <h2 id="kb-all" className="mb-2.5 flex items-center gap-2 text-sm font-semibold tracking-tight">
              <BookOpen className="size-3.5 text-muted-foreground" aria-hidden />
              All articles
              <Badge variant="neutral" size="sm">
                {all.length}
              </Badge>
            </h2>
            {all.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                title="No articles yet"
                description="Nothing has been published to your organisation. Raise a ticket and we will help directly."
              />
            ) : (
              <ArticleGrid articles={[...all].sort((a, b) => a.title.localeCompare(b.title))} />
            )}
          </section>

          <CategoriesNotYet />
        </>
      )}
    </>
  )
}

function ArticleGrid({ articles }: { articles: KbArticleSummary[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((article) => (
        <li key={article.slug}>
          <Link to={`/kb/${article.slug}`} className="block h-full rounded-lg">
            <Card className="group h-full transition-colors hover:border-primary/40 hover:bg-accent/30">
              <CardContent className="flex h-full flex-col p-4">
                <h3 className="text-sm font-medium text-foreground">{article.title}</h3>
                <p className="mt-1.5 flex-1 text-xs text-muted-foreground">
                  Updated {formatDate(article.updated_at)}
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-primary">
                  Read
                  <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
                    →
                  </span>
                </span>
              </CardContent>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/** The eleven categories the brief asks for, shown as what they are: a plan.
 *
 *  Rendering them as clickable filters that all return everything would be a
 *  lie the reader discovers on the first click. */
function CategoriesNotYet() {
  return (
    <section aria-labelledby="kb-categories">
      <h2 id="kb-categories" className="mb-2.5 text-sm font-semibold tracking-tight">
        Categories
      </h2>
      <Card className="border-dashed">
        <CardContent className="p-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Articles are not categorised yet — <code>KbArticle</code> has a title, a body, a
            visibility and a published flag, and no category or tag. These are the categories
            planned; until the field exists, browsing is by search and recency rather than by a
            filter that would return everything.
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {TARGET_CATEGORIES.map((category) => (
              <li
                key={category}
                className="rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground"
              >
                {category}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  )
}

/* ------------------------------------------------------------- article */

export function KbArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['kb-article', slug],
    queryFn: () => api.get<PortalArticleDetail>(`/kb/${slug}/`),
  })
  const { data: all = [] } = useQuery({
    queryKey: ['kb'],
    queryFn: () => api.get<KbArticleSummary[]>('/kb/'),
  })

  const body = data?.body_md ?? ''
  const toc = useMemo(() => tableOfContents(body), [body])
  const html = useMemo(() => withHeadingIds(body), [body])
  const related = useMemo(() => (data ? relatedArticles(all, data) : []), [all, data])

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <ErrorNote message="That article is not available to you. It may be unpublished, or restricted to internal staff." />
    )
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast({ title: 'Link copied', variant: 'success' })
    } catch {
      // Clipboard access can be refused outright — an insecure origin, or a
      // browser policy. Saying so beats a button that appears to do nothing.
      toast({ title: 'Your browser would not let us copy', variant: 'critical' })
    }
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Knowledge base', to: '/kb' },
          { label: data.title },
        ]}
      />

      <div className="mt-3 grid gap-6 lg:grid-cols-[1fr_15rem]">
        <article className="min-w-0">
          <PageHeader title={data.title} />

          <div className="-mt-2 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden />
              {readingTime(body)} min read
            </span>
            <span>Updated {formatDate(data.updated_at)}</span>
            <Unavailable label="Author" why="KbArticle has an author and the agent API returns it. PortalArticleSerializer does not, so a reader cannot see who wrote this." />
            <Unavailable label="Version" why="Articles carry no version number. Every edit is recorded with its before and after, and the service desk can read that history — but it is internal, and the portal deliberately does not expose who edited what." />
          </div>

          <Card>
            <CardContent className="pt-5">
              <RichText
                html={html}
                className="[&_h1]:mb-2 [&_h1]:mt-5 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1:first-child]:mt-0 [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2:first-child]:mt-0 [&_h3]:mb-1.5 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_h4]:mb-1.5 [&_h4]:mt-3 [&_h4]:text-sm [&_h4]:font-medium [&_pre]:mb-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_table]:mb-3 [&_table]:w-full [&_table]:text-left [&_td]:border-t [&_td]:border-border [&_td]:py-1.5 [&_td]:pr-3 [&_th]:py-1.5 [&_th]:pr-3 [&_th]:font-medium [&_*]:scroll-mt-20"
              />
            </CardContent>
          </Card>

          <Helpful slug={data.slug} answer={data.my_feedback} />

          {related.length > 0 && (
            <section aria-labelledby="kb-related" className="mt-4">
              <h2 id="kb-related" className="mb-2.5 text-sm font-semibold tracking-tight">
                Related articles
              </h2>
              <ArticleGrid articles={related} />
              <p className="mt-2 text-xs text-muted-foreground">
                Matched on words shared with this title. A curated list would need a relationship
                between articles, which the model does not have.
              </p>
            </section>
          )}
        </article>

        {/* ------------------------------------------------------ rail */}
        <aside className="print:hidden">
          <div className="sticky top-20 space-y-4">
            {toc.length > 0 && (
              <nav aria-labelledby="kb-toc">
                <h2 id="kb-toc" className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  On this page
                </h2>
                <ul className="space-y-0.5 border-l border-border">
                  {toc.map((entry) => (
                    <li key={entry.id}>
                      <a
                        href={`#${entry.id}`}
                        className={cn(
                          '-ml-px block border-l-2 border-transparent py-1 text-xs text-muted-foreground transition-colors hover:border-border hover:text-foreground',
                          entry.level === 1 || entry.level === 2 ? 'pl-3' : 'pl-6',
                        )}
                      >
                        {entry.text}
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                  Built from this article&apos;s headings.
                </p>
              </nav>
            )}

            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Share
              </h2>
              <div className="flex flex-wrap gap-1.5">
                <Button variant="outline" size="sm" onClick={copyLink}>
                  <Link2 />
                  Copy link
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer />
                  Print
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a
                    href={`mailto:?subject=${encodeURIComponent(data.title)}&body=${encodeURIComponent(window.location.href)}`}
                  >
                    <Share2 />
                    Email
                  </a>
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <Button variant="outline" size="sm" disabled className="pointer-events-none">
                        <FileDown />
                        PDF
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[16rem]">
                    A fixed-layout PDF needs rendering server-side. Print above uses the browser and
                    the article&apos;s print stylesheet, which is the honest version of this today.
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Coming later
              </h2>
              <ul className="space-y-1">
                {[
                  ['AI answers', 'Semantic search over article bodies'],
                  ['Microsoft Copilot', 'Grounding Copilot in this knowledge base'],
                  ['Teams search', 'Finding articles without leaving Teams'],
                  ['Outlook add-in', 'Suggesting an article while replying'],
                ].map(([label, why]) => (
                  <li key={label} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                    <Sparkles className="mt-0.5 size-3 shrink-0 opacity-50" aria-hidden />
                    <span>
                      <span className="block font-medium">{label}</span>
                      <span className="block opacity-80">{why}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </>
  )
}

/** "Was this article helpful?" — the reader's own answer.
 *
 * This replaces a widget that was rendered disabled, with a note saying a
 * thumbs-up which silently discards the click is worse than one that admits it
 * cannot take it. It can take it now.
 *
 * Three things it is careful about:
 *
 * **It shows your thumb, not a score.** How many other people agreed is the
 * editor's question and lives on the agent KB screen. A reader told that 80% of
 * people disagreed with them is being argued with.
 *
 * **Clicking your existing answer withdraws it.** The buttons are the only
 * control there is, so somebody who clicked by accident needs a way back.
 *
 * **A failed save says so and keeps the article.** The answer is a footnote to
 * the page; losing the page because a footnote failed would be the wrong trade.
 */
function Helpful({ slug, answer }: { slug: string; answer: Answer }) {
  const queryClient = useQueryClient()
  const [current, setCurrent] = useState<Answer>(answer)

  const save = useMutation({
    mutationFn: (next: Answer) =>
      next === null
        ? api.del<FeedbackResponse>(`/kb/${slug}/feedback/`)
        : api.putJson<FeedbackResponse>(`/kb/${slug}/feedback/`, { helpful: next }),
    onSuccess: (result) => {
      setCurrent(result.my_feedback)
      void queryClient.invalidateQueries({ queryKey: ['kb-article', slug] })
    },
  })

  const state = feedbackState({
    isPending: save.isPending,
    isError: save.isError,
    answer: current,
  })

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Was this article helpful?</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          {[true, false].map((value) => (
            <Button
              key={String(value)}
              variant={isChosen(current, value) ? 'default' : 'outline'}
              size="sm"
              aria-pressed={isChosen(current, value)}
              disabled={save.isPending}
              onClick={() => save.mutate(nextAnswer(current, value))}
            >
              {value ? <ThumbsUp /> : <ThumbsDown />}
              {value ? 'Yes' : 'No'}
            </Button>
          ))}
          <p
            className={
              state === 'error'
                ? 'text-xs text-critical'
                : 'text-xs text-muted-foreground'
            }
          >
            {feedbackMessage(state, current)}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function Unavailable({ label, why }: { label: string; why: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
          <span className="rounded-full border border-dashed border-border px-1.5 text-[10px]">
            not set
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[18rem]">
        {why}
      </TooltipContent>
    </Tooltip>
  )
}
